import { useContext } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AppVersion, ORGANIZATION_VIEWER_VERSION_KEY } from '@core/constants/appVersion';
import { ORGANIZATION_SETTINGS_QUERY_KEY } from '@core/hooks/useOrganizationSettings';
import { AuthContext } from '@core/providers/AuthProvider';
import isCustomErroHandlerResponse from '@core/utils/isCustomErrorHandlerResponse';
import { setViewerVersionSetting } from '../api/setViewerVersionSetting';

const useSetViewerVersionSetting = () => {
  const { organization } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const queryKey = [ORGANIZATION_SETTINGS_QUERY_KEY, organization.organizationId];

  // Deliberately NOT optimistic: AppVersionGuard reads this same cache, so an
  // optimistic write redirected to the other app the instant the switch was
  // clicked — while the PUT was still in flight. The navigation aborted the
  // request, the setting was never saved, and the other app read the old
  // value. The cache (and therefore the redirect) only changes once the save
  // is confirmed.
  const { isLoading: isSaving, mutateAsync: setViewerVersionAsync } = useMutation({
    mutationFn: (version: AppVersion) => setViewerVersionSetting(version),
    onSuccess: (data, version) => {
      if (isCustomErroHandlerResponse(data)) {
        return;
      }
      queryClient.setQueryData(queryKey, (previousSettings?: Record<string, unknown>) => ({
        ...(previousSettings || {}),
        [ORGANIZATION_VIEWER_VERSION_KEY]: version,
      }));
    },
  });

  return { isSaving, setViewerVersionAsync };
};

export default useSetViewerVersionSetting;
